import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Info,
  Plug,
  Plus,
  RefreshCw,
  Unplug,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { DiscordAdvancedAutomatedMessages } from './DiscordAdvancedAutomatedMessages';
import { DiscordBotHealthLine } from './DiscordHealthLine';
import {
  DiscordTokenInputClassification,
  buildDiscordBotSettingsUrl,
  buildDiscordInviteUrl,
  classifyDiscordTokenInput,
  deriveDiscordApplicationId,
} from './discordApplicationId';
import {
  DISCORD_INTEGRATION_NAME,
  DISCORD_QUERY_KEY,
  type DiscordIntegrationParams,
  type DiscordIntegrationResponse,
  LATE_CALLBACK_POLL_WINDOW_MS,
  SYNC_POLL_INTERVAL_MS,
  SYNC_TIMEOUT_MS,
} from './discordIntegration';
import type {
  DiscordBotStatus,
  DiscordGuildHealth,
  DiscordGuildSummary,
} from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { IntegrationEnabledBadge } from '@/Domain/components/Integration/IntegrationEnabledBadge';
import { IntegrationStatusBadge } from '@/Domain/components/Integration/IntegrationStatusBadge';
import { IntegrationConnectDialog } from '@/Domain/components/Integration/components/IntegrationConnectDialog';
import { IntegrationConnectionCard } from '@/Domain/components/Integration/components/IntegrationConnectionCard';
import { IntegrationIntroduction } from '@/Domain/components/Integration/components/IntegrationIntroduction';
import { IntegrationSection } from '@/Domain/components/Integration/components/IntegrationSection';
import { Alert, AlertDescription, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { DiscordIcon } from '@/Domain/components/icons/Discord.icon';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import {
  trackIntegrationConnectFailed,
  trackIntegrationConnectStarted,
  trackIntegrationConnectSucceeded,
} from '@/Domain/hooks/Integration/integrationConnectAnalytics';
import { cn } from '@/Domain/utils/cn';

const DISCORD_ERROR_INVALID_TOKEN = 'InvalidToken';
const DISCORD_ERROR_PRIVILEGED_INTENTS = 'PrivilegedIntentsRequired';
const DISCORD_ERROR_DUPLICATE_APPLICATION = 'DuplicateApplication';

// Map Discord's raw bot-error string to a bounded `reason` for the unified
// connect funnel (raw detail stays on `discord_integration_connect_result`).
const discordConnectFailureReason = (errors: string | null | undefined, status: string): string => {
  if (errors === DISCORD_ERROR_INVALID_TOKEN) return 'invalid_token';
  if (errors === DISCORD_ERROR_PRIVILEGED_INTENTS) return 'missing_message_content_intent';
  if (errors === DISCORD_ERROR_DUPLICATE_APPLICATION) return 'duplicate_application';
  if (errors) return 'bot_error';
  return status; // bounded DiscordConnectStatus: 'failed' | 'disabled'
};

type DiscordTimeoutReason = 'bot_error' | 'state_mismatch' | 'no_callback' | 'save_pending';
const discordTimeoutReason = (
  errors: string | null,
  active: boolean,
  requestedActive: boolean | null,
  saved: boolean,
): DiscordTimeoutReason => {
  if (!saved) return 'save_pending';
  if (errors) return 'bot_error';
  if (requestedActive !== null && active !== requestedActive) return 'state_mismatch';
  return 'no_callback';
};
const DISCORD_DOCS_URL = 'https://docs.quickchat.ai/channels/discord';
const DISCORD_SERVER_INVITE_URL = 'https://discord.gg/KqkHwvPRNH';
const DISCORD_DEV_PORTAL_URL = 'https://discord.com/developers/applications';
// Deep links to the docs troubleshooting subheadings; slugs must match the headings
// on docs.quickchat.ai/channels/discord (cross-repo contract, kept in sync with the docs PR).
const DISCORD_DOCS_ANCHORS = {
  messageContentIntent: `${DISCORD_DOCS_URL}#message-content-intent`,
  invalidToken: `${DISCORD_DOCS_URL}#invalid-bot-token`,
  codeGrant: `${DISCORD_DOCS_URL}#require-oauth2-code-grant`,
  coexistence: `${DISCORD_DOCS_URL}#both-bots-are-in-the-same-server`,
} as const;
const LINK_CLASS =
  'text-foreground link-underline underline-offset-2 transition-colors hover:text-muted-foreground';
const TROUBLESHOOT_LINK_CLASS =
  'link-underline underline-offset-2 transition-opacity hover:opacity-70';

// Settle only updates that cannot start or stop a gateway process.
const SYNC_SETTLE_MS = 6_000;

// Users paste (not type) tokens, so one check per paste; long enough to skip
// intermediate keystrokes when they do type.
const TOKEN_CHECK_DEBOUNCE_MS = 500;
// Wrong-paste shapes that can never be a bot token; blocked client-side with
// specific guidance instead of a doomed 20s connect attempt.
const DEFINITIVE_MISPASTE: DiscordTokenInputClassification[] = [
  'application_id',
  'client_secret',
  'url',
];

type DiscordTokenCheckStatus = 'valid' | 'invalid' | 'unknown';

type DiscordMessageContentIntent = 'on' | 'off' | 'unknown';

interface DiscordTokenCheckResult {
  status: DiscordTokenCheckStatus;
  name: string;
  username: string;
  message_content_intent?: DiscordMessageContentIntent;
}

// A getMe verdict pinned to the exact input it was produced for, so a stale
// response can never gate or describe a newer paste.
interface DiscordTokenCheck extends DiscordTokenCheckResult {
  token: string;
}

// Bot-restarting action shown as a spinner while we poll for fresh status.
type DiscordSyncAction = 'starting' | 'stopping' | null;
// Terminal outcome reported to PostHog when the post-save poll resolves.
type DiscordConnectStatus = 'connected' | 'failed' | 'disabled' | 'timeout';
type DiscordSyncResolution = 'callback' | 'settled' | 'timeout';

interface DiscordPatchVariables {
  name: typeof DISCORD_INTEGRATION_NAME;
  params: Partial<DiscordIntegrationParams>;
}

const discordFormSchema = z.object({
  discord_bot_token: z.string(),
});

type DiscordFormValues = z.infer<typeof discordFormSchema>;

export interface DiscordAdvancedSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Returns to the one-click sheet; closing the sheet any other way closes Discord entirely. */
  onBack: () => void;
  /** Palette for the portaled content, which renders outside any themed shell the caller sets up. */
  contentClassName?: string;
}

export const DiscordAdvancedSheet = ({
  open,
  onOpenChange,
  onBack,
  contentClassName,
}: DiscordAdvancedSheetProps) => {
  const { t } = useTranslation(['integration']);
  const queryClient = useQueryClient();
  const { capture } = usePostHogAnalytics();
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);

  const [syncing, setSyncing] = useState(false);
  const [syncUnconfirmed, setSyncUnconfirmed] = useState(false);
  const [statusBaselineReady, setStatusBaselineReady] = useState(false);
  const syncBaselineRef = useRef<number | null>(null);
  const syncActionRef = useRef<DiscordSyncAction>(null);
  // The header badge stays on its pre-change value until Discord confirms, so the
  // process is never "confirmed" mid-flight. Captured when a sync starts.
  const enabledBeforeSyncRef = useRef(false);
  // The enable/disable toast is deferred to the confirm point (not the PATCH), so
  // it only fires once Discord actually applied the change.
  const pendingSyncToastRef = useRef<string | undefined>(undefined);
  // Latest bot errors, read by the hard-timeout callback (whose closure is stale)
  // to decide whether the deferred toast is a success or should be suppressed.
  const errorsRef = useRef<string | null>(null);
  const activeRef = useRef(false);
  const stateMatchedAtRequestRef = useRef(false);
  const canSettleWithoutCallbackRef = useRef(false);
  const requestedActiveRef = useRef<boolean | null>(null);
  const syncSavedRef = useRef(false);
  const syncStartedAtRef = useRef(0);
  const lateCallbackPollUntilRef = useRef(0);
  const syncFinishedRef = useRef(true);
  const syncInFlightRef = useRef(false);
  const patchInFlightRef = useRef(false);
  const patchErrorStatusPendingRef = useRef(false);
  const gatewayCallCompletedRef = useRef(false);
  const serverSyncWasPendingRef = useRef(false);
  // True between a connect attempt (handleConnect) and its poll/timeout/PATCH-error
  // outcome, so the unified funnel's succeeded/failed only count real connects —
  // not enable/disable toggles or settings saves that share these handlers.
  const connectAttemptRef = useRef(false);
  const connectAttemptIdRef = useRef(0);
  const sheetOpenCapturedRef = useRef(false);

  const {
    data,
    isFetching: statusFetching,
    isRefetchError: statusRefetchError,
    isSuccess: statusFetchSucceeded,
    dataUpdatedAt: statusUpdatedAt,
  } = useQuery<DiscordIntegrationResponse>({
    queryKey: DISCORD_QUERY_KEY,
    enabled: open,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const queryData = query.state.data as DiscordIntegrationResponse | undefined;
      return syncing ||
        (syncUnconfirmed && Date.now() < lateCallbackPollUntilRef.current) ||
        queryData?.discord_sync_pending
        ? SYNC_POLL_INTERVAL_MS
        : false;
    },
  });

  useEffect(() => {
    if (!open) {
      setStatusBaselineReady(false);
      return;
    }
    if (!statusFetching && !statusRefetchError && statusFetchSucceeded) {
      setStatusBaselineReady(true);
    }
  }, [open, statusFetching, statusFetchSucceeded, statusRefetchError]);

  // One-click servers keep using the canonical bot until removed, so surface a heads-up here.
  const { data: guilds } = useQuery<DiscordGuildSummary[]>({
    queryKey: [QueryKey.integration.discordGuilds],
    enabled: open,
  });
  // Same query key as the one-click sheet, so this costs no extra request.
  const { data: guildsHealth, isError: guildsHealthError } = useQuery<{
    guilds: DiscordGuildHealth[];
  }>({
    queryKey: [QueryKey.integration.discordGuildsHealth],
    enabled: open,
  });
  const absentGuildIds = useMemo(
    () =>
      new Set(
        guildsHealthError
          ? []
          : (guildsHealth?.guilds ?? [])
              .filter((entry) => entry.present === false)
              .map((entry) => entry.guild_id),
      ),
    [guildsHealth, guildsHealthError],
  );
  // Only ACTIVE + enabled guilds actually route to the shared bot, so only those hold a
  // server back from the own bot; a disabled or pending row is not in the way, and neither
  // is one Discord says the shared bot has already been kicked from.
  const oneClickServers = (guilds ?? []).filter(
    (guild) => guild.state === 'ACTIVE' && guild.enabled && !absentGuildIds.has(guild.guild_id),
  );

  const params = data?.params;
  const serverSyncPending = Boolean(data?.discord_sync_pending);

  const releaseUnconfirmedSync = useCallback(() => {
    lateCallbackPollUntilRef.current = 0;
    syncInFlightRef.current = false;
    setSyncUnconfirmed(false);
  }, []);

  // Bot profile + health are fetched live (never persisted) each time the sheet opens
  // while connected, so they reflect the current Discord profile and a status we just
  // verified rather than the gateway flags, which only refresh on a settings push.
  const {
    data: botInfo,
    isPending: botInfoPending,
    isError: botInfoError,
  } = useQuery<DiscordBotStatus>({
    queryKey: [QueryKey.integration.discordBotInfo],
    enabled: open && Boolean(params?.discord_bot_token),
  });

  useEffect(() => {
    errorsRef.current = params?.discord_bot_errors ?? null;
    activeRef.current = Boolean(params?.discord_bot_active);
  }, [params?.discord_bot_errors, params?.discord_bot_active]);

  useEffect(() => {
    if (data?.discord_sync_pending === true) {
      serverSyncWasPendingRef.current = true;
      return;
    }
    if (data?.discord_sync_pending === false && serverSyncWasPendingRef.current) {
      serverSyncWasPendingRef.current = false;
      gatewayCallCompletedRef.current = true;
      if (syncFinishedRef.current) releaseUnconfirmedSync();
    }
  }, [data?.discord_sync_pending, releaseUnconfirmedSync]);

  useEffect(() => {
    if (!patchErrorStatusPendingRef.current || data?.discord_sync_pending !== false) {
      return;
    }
    patchErrorStatusPendingRef.current = false;
    gatewayCallCompletedRef.current = true;
    if (syncFinishedRef.current) releaseUnconfirmedSync();
  }, [data?.discord_sync_pending, statusUpdatedAt, releaseUnconfirmedSync]);

  const initialValues = useMemo<DiscordFormValues>(
    () => ({ discord_bot_token: params?.discord_bot_token ?? '' }),
    [params],
  );

  const form = useForm<DiscordFormValues>({
    resolver: zodResolver(discordFormSchema),
    defaultValues: initialValues,
    values: initialValues,
  });

  useEffect(() => {
    if (!open) {
      connectAttemptIdRef.current += 1;
      setConnectDialogOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      sheetOpenCapturedRef.current = false;
      return;
    }
    if (sheetOpenCapturedRef.current) return;
    sheetOpenCapturedRef.current = true;
    capture(PostHogEventEnum.DiscordIntegrationSheetOpened);
  }, [open, capture]);

  const finishSync = useCallback(
    (status: DiscordConnectStatus, reason: string | null, resolution: DiscordSyncResolution) => {
      if (syncFinishedRef.current) return;
      syncFinishedRef.current = true;
      const unconfirmed = resolution === 'timeout' && !gatewayCallCompletedRef.current;
      lateCallbackPollUntilRef.current = unconfirmed
        ? Date.now() + LATE_CALLBACK_POLL_WINDOW_MS
        : 0;
      syncInFlightRef.current = unconfirmed;
      setSyncUnconfirmed(unconfirmed);
      capture(PostHogEventEnum.DiscordIntegrationConnectResult, {
        status,
        reason,
        action: syncActionRef.current,
        resolution,
        state_matched_at_request: stateMatchedAtRequestRef.current,
      });
      if (connectAttemptRef.current) {
        if (status === 'connected') {
          trackIntegrationConnectSucceeded(capture, 'discord');
        } else if (status !== 'timeout') {
          trackIntegrationConnectFailed(
            capture,
            'discord',
            discordConnectFailureReason(status === 'failed' ? reason : null, status),
          );
        }
        connectAttemptRef.current = false;
      }
      setSyncing(false);
      syncActionRef.current = null;
      const persisted = resolution === 'callback' || syncSavedRef.current;
      if (resolution === 'timeout') {
        toast.info(t('integration:discord.sync_timeout'));
      } else if (persisted && status !== 'failed') {
        toast.success(pendingSyncToastRef.current ?? t('integration:discord.saved'));
      }
      pendingSyncToastRef.current = undefined;
    },
    [capture, t],
  );
  const finishSyncRef = useRef(finishSync);
  useEffect(() => {
    finishSyncRef.current = finishSync;
  }, [finishSync]);

  const settleSyncIfSafe = useCallback(() => {
    if (!syncSavedRef.current) return;
    if (!canSettleWithoutCallbackRef.current) return;
    if (errorsRef.current) return;
    if (activeRef.current !== requestedActiveRef.current) return;
    finishSyncRef.current(activeRef.current ? 'connected' : 'disabled', null, 'settled');
  }, []);

  // Stop the post-save poll and report the outcome once Discord confirms a
  // fresh status (discord_last_updated advances past the baseline).
  useEffect(() => {
    if (!syncing && !syncUnconfirmed) return;
    const updatedMs = params?.discord_last_updated ? Date.parse(params.discord_last_updated) : null;
    const advanced =
      syncBaselineRef.current === null
        ? updatedMs !== null
        : updatedMs !== null && updatedMs > syncBaselineRef.current;
    if (!advanced) return;
    // The handshake this line reports on just moved, so re-ask rather than leaving it
    // on the "not confirmed yet" it was correctly showing a moment ago.
    queryClient.invalidateQueries({ queryKey: [QueryKey.integration.discordBotInfo] });
    const hasError = Boolean(params?.discord_bot_errors);
    if (syncUnconfirmed) {
      lateCallbackPollUntilRef.current = 0;
      syncInFlightRef.current = false;
      setSyncUnconfirmed(false);
      return;
    }
    const requestedActive = requestedActiveRef.current;
    if (
      !hasError &&
      requestedActive !== null &&
      Boolean(params?.discord_bot_active) !== requestedActive
    ) {
      return;
    }
    const status: DiscordConnectStatus = params?.discord_bot_errors
      ? 'failed'
      : params?.discord_bot_active
        ? 'connected'
        : 'disabled';
    finishSync(status, params?.discord_bot_errors || null, 'callback');
  }, [
    params?.discord_last_updated,
    params?.discord_bot_active,
    params?.discord_bot_errors,
    syncing,
    syncUnconfirmed,
    finishSync,
    queryClient,
  ]);

  useEffect(() => {
    if (!syncing) return;
    const settleId = setTimeout(settleSyncIfSafe, SYNC_SETTLE_MS);
    const timeoutId = setTimeout(() => {
      finishSyncRef.current(
        'timeout',
        discordTimeoutReason(
          errorsRef.current,
          activeRef.current,
          requestedActiveRef.current,
          syncSavedRef.current,
        ),
        'timeout',
      );
    }, SYNC_TIMEOUT_MS);
    return () => {
      clearTimeout(settleId);
      clearTimeout(timeoutId);
    };
  }, [syncing, settleSyncIfSafe]);

  const isConnected = Boolean(params?.discord_bot_token);
  const isEnabled = Boolean(params?.discord_bot_active);
  const botNotRunning = isConnected && Boolean(params?.discord_bot_errors);
  // The failing shape Ludeo hit: an own bot that is up, silently outranked in every server
  // the shared bot still holds. Both bots merely coexisting elsewhere is fine and stays info.
  const ownBotShadowed = isConnected && isEnabled && oneClickServers.length > 0;

  const invalidateAdvancedBotQueries = (variables: DiscordPatchVariables) => {
    if (!('discord_bot_token' in variables.params)) return;
    queryClient.invalidateQueries({
      queryKey: [QueryKey.integration.discordAdvancedGuilds],
    });
    queryClient.invalidateQueries({
      queryKey: [QueryKey.integration.discordAdvancedAutomatedMessageOptions],
    });
  };

  const handlePatchSuccess = (
    responseData: DiscordIntegrationResponse,
    variables: DiscordPatchVariables,
  ) => {
    // The bot status updates asynchronously via Discord's callback, so the PATCH
    // response status is stale (still "active", no error). Caching it lets a stale
    // "active" mask a later InvalidToken and show a false Connected — refetch the
    // real server state instead. The polling effect then converges on the truth.
    queryClient.invalidateQueries({ queryKey: DISCORD_QUERY_KEY });
    // The AI Action dropdown gates the {{discord_bot_token}} System Token on this boolean status;
    // refresh it so connecting/clearing the token reflects without waiting out its 10-min cache.
    queryClient.invalidateQueries({
      queryKey: [QueryKey.integration.discordTokenStatus],
    });
    invalidateAdvancedBotQueries(variables);
    queryClient.invalidateQueries({
      queryKey: [QueryKey.integration.discordBotInfo],
    });
    form.reset({
      ...form.getValues(),
      discord_bot_token: responseData.params.discord_bot_token ?? '',
    });
    // Close the connect/reconnect dialog once the token save succeeds; the
    // bot-restart spinner then plays out in the sheet behind it.
    setConnectDialogOpen(false);
  };

  const handlePatchError = (
    error: AxiosError<{ detail?: string }>,
    variables: DiscordPatchVariables,
  ) => {
    patchErrorStatusPendingRef.current = true;
    queryClient.invalidateQueries({ queryKey: DISCORD_QUERY_KEY });
    invalidateAdvancedBotQueries(variables);
    // Surface the backend `detail` message when available; never include
    // the raw request body (which may carry the bot token).
    const detail = error.response?.data?.detail;
    capture(PostHogEventEnum.DiscordIntegrationSaveFailed, { reason: detail ?? 'unknown' });
    if (connectAttemptRef.current) {
      trackIntegrationConnectFailed(capture, 'discord', 'save_failed');
      connectAttemptRef.current = false;
    }
    toast.error(detail ?? t('integration:discord.save_failed'));
  };

  const patchMutationConfig = {
    mutationKey: [QueryKey.integration.patch],
    onSuccess: handlePatchSuccess,
    onError: handlePatchError,
    onSettled: () => {
      patchInFlightRef.current = false;
    },
  };

  // Keep disconnect state separate while sharing the integration PATCH callbacks.
  const mutation = useMutation<
    DiscordIntegrationResponse,
    AxiosError<{ detail?: string }>,
    DiscordPatchVariables
  >(patchMutationConfig);

  const disconnectMutation = useMutation<
    DiscordIntegrationResponse,
    AxiosError<{ detail?: string }>,
    DiscordPatchVariables
  >(patchMutationConfig);
  const patchPending = mutation.isPending || disconnectMutation.isPending;
  const interactionBlocked =
    !statusBaselineReady || syncing || syncUnconfirmed || serverSyncPending || patchPending;

  // Auto-save a single changed field immediately (no Save button). When the
  // change restarts the bot (enable toggle, or a token change while active),
  // show a spinner and poll until the Discord server reports fresh status —
  // mirroring the legacy modal's poll-until-confirmed flow.
  const persistParams = (
    partial: Partial<DiscordIntegrationParams>,
    syncActionForChange: DiscordSyncAction = null,
    successToast?: string,
    patchMutation: typeof mutation = mutation,
  ) => {
    if (Object.keys(partial).length === 0) return;
    if (patchInFlightRef.current || interactionBlocked) return;
    patchInFlightRef.current = true;
    // Show the preloader and start polling immediately (before the network
    // round-trip) so the toggle feels instant; revert if the save fails.
    if (syncActionForChange) {
      syncBaselineRef.current = params?.discord_last_updated
        ? Date.parse(params.discord_last_updated)
        : null;
      const requestedActive = partial.discord_bot_active;
      const currentActive = Boolean(params?.discord_bot_active);
      // Safe settling assumes the app and gateway stored tokens were already in sync.
      const tokenUnchanged =
        partial.discord_bot_token === undefined ||
        (partial.discord_bot_token ?? '') === (params?.discord_bot_token ?? '');
      requestedActiveRef.current = requestedActive ?? null;
      stateMatchedAtRequestRef.current =
        requestedActive !== undefined && requestedActive === currentActive;
      canSettleWithoutCallbackRef.current =
        stateMatchedAtRequestRef.current && (requestedActive === false || tokenUnchanged);
      syncSavedRef.current = false;
      syncStartedAtRef.current = Date.now();
      syncFinishedRef.current = false;
      syncInFlightRef.current = true;
      gatewayCallCompletedRef.current = false;
      serverSyncWasPendingRef.current = false;
      setSyncUnconfirmed(false);
      // Freeze the badge on its current value and stash the toast until Discord
      // confirms — nothing is announced mid-flight.
      enabledBeforeSyncRef.current = Boolean(params?.discord_bot_active);
      pendingSyncToastRef.current = successToast;
      syncActionRef.current = syncActionForChange;
      setSyncing(true);
    }
    patchMutation.mutate(
      { name: DISCORD_INTEGRATION_NAME, params: partial },
      {
        onSuccess: (responseData) => {
          if (syncActionForChange) {
            syncSavedRef.current = true;
            gatewayCallCompletedRef.current = responseData.discord_sync_pending === false;
            if (
              syncFinishedRef.current &&
              syncInFlightRef.current &&
              gatewayCallCompletedRef.current
            ) {
              releaseUnconfirmedSync();
            }
            if (Date.now() - syncStartedAtRef.current >= SYNC_SETTLE_MS) {
              settleSyncIfSafe();
            }
          }
          // Sync actions defer their toast to the confirm point; immediate
          // settings saves (no sync) confirm here.
          if (!syncActionForChange) {
            toast.success(successToast ?? t('integration:discord.saved'));
          }
        },
        onError: () => {
          if (syncActionForChange) {
            syncFinishedRef.current = true;
            syncInFlightRef.current = false;
            if (gatewayCallCompletedRef.current) releaseUnconfirmedSync();
            syncActionRef.current = null;
            setSyncing(false);
            pendingSyncToastRef.current = undefined;
          }
        },
      },
    );
  };

  const tokenValue = form.watch('discord_bot_token');
  const trimmedToken = (tokenValue ?? '').trim();
  const tokenClassification = useMemo(
    () => classifyDiscordTokenInput(trimmedToken),
    [trimmedToken],
  );

  // Derive the Discord application ID from the saved bot token to build the
  // "Add to a Discord Server" invite link shown in the connection card.
  const applicationId = useMemo(
    () => deriveDiscordApplicationId((params?.discord_bot_token || '').trim()),
    [params?.discord_bot_token],
  );

  // Latest trimmed input, read by mutation callbacks to drop stale verdicts
  // (the user may have retyped while a getMe round-trip was in flight).
  const trimmedTokenRef = useRef('');
  const [tokenCheck, setTokenCheck] = useState<DiscordTokenCheck | null>(null);
  // Covers the pre-save check inside handleConnect, before the PATCH's own
  // isPending takes over, so the submit spinner never flickers off mid-connect.
  const [connecting, setConnecting] = useState(false);
  // One token_validated capture per distinct (input, result) pair — the debounce
  // plus this guard keep the event at paste-cardinality, not keystroke-cardinality.
  const lastValidationCaptureRef = useRef<string | null>(null);
  // Pending validation lets a fast Connect click avoid a duplicate request.
  const pendingCheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const captureTokenValidated = useCallback(
    (result: string, token: string, intent: DiscordMessageContentIntent = 'unknown') => {
      const key = `${token}:${result}:${intent}`;
      if (lastValidationCaptureRef.current === key) return;
      lastValidationCaptureRef.current = key;
      capture(PostHogEventEnum.DiscordIntegrationTokenValidated, {
        result,
        message_content_intent: intent,
      });
    },
    [capture],
  );

  const validateMutation = useMutation<DiscordTokenCheckResult, AxiosError, { token: string }>({
    mutationKey: [QueryKey.integration.discordValidateToken],
    onSuccess: (result, variables) => {
      if (variables.token !== trimmedTokenRef.current) return;
      setTokenCheck({ token: variables.token, ...result });
      captureTokenValidated(
        result.status,
        variables.token,
        result.message_content_intent ?? 'unknown',
      );
    },
    onError: (_error, variables) => {
      // Our own backend failed (not Discord rejecting the token): record an
      // 'unknown' verdict so the UI goes quiet and connect fails open. Emit it
      // too, so the funnel's fail-open rate is visible (the event contract lists
      // 'unknown'), same as a backend-reported 'unknown'.
      if (variables.token !== trimmedTokenRef.current) return;
      setTokenCheck({ token: variables.token, status: 'unknown', name: '', username: '' });
      captureTokenValidated('unknown', variables.token);
    },
  });
  const { mutateAsync: validateTokenAsync } = validateMutation;
  const pendingValidationRef = useRef<{
    token: string;
    promise: Promise<DiscordTokenCheckResult>;
  } | null>(null);

  const validateTokenOnce = useCallback(
    (token: string): Promise<DiscordTokenCheckResult> => {
      if (pendingValidationRef.current?.token === token) {
        return pendingValidationRef.current.promise;
      }
      const promise = validateTokenAsync({ token });
      pendingValidationRef.current = { token, promise };
      const clearPending = () => {
        if (pendingValidationRef.current?.promise === promise) pendingValidationRef.current = null;
      };
      promise.then(clearPending, clearPending);
      return promise;
    },
    [validateTokenAsync],
  );

  // Follows a fix in the Developer Portal, so an earlier request must never answer it.
  const recheckToken = () => {
    if (!trimmedToken || validateMutation.isPending) return;
    pendingValidationRef.current = null;
    void validateTokenOnce(trimmedToken).catch(() => undefined);
  };

  // Debounced paste-time check: a plain effect timer so every input change
  // resets the countdown and closing the dialog cancels any pending check.
  useEffect(() => {
    trimmedTokenRef.current = trimmedToken;
    setTokenCheck(null);
    if (!connectDialogOpen || !trimmedToken) return;
    if (DEFINITIVE_MISPASTE.includes(tokenClassification)) {
      // Structurally impossible tokens (App ID / secret / URL) are explained
      // and blocked client-side; no point in asking Discord.
      captureTokenValidated(tokenClassification, trimmedToken);
      return;
    }
    const id = setTimeout(() => {
      pendingCheckTimerRef.current = null;
      void validateTokenOnce(trimmedToken).catch(() => undefined);
    }, TOKEN_CHECK_DEBOUNCE_MS);
    pendingCheckTimerRef.current = id;
    return () => {
      clearTimeout(id);
      if (pendingCheckTimerRef.current === id) pendingCheckTimerRef.current = null;
    };
  }, [
    connectDialogOpen,
    trimmedToken,
    tokenClassification,
    validateTokenOnce,
    captureTokenValidated,
  ]);

  const tokenCheckMatches = tokenCheck?.token === trimmedToken ? tokenCheck : null;
  const messageContentIntentOff =
    tokenCheckMatches?.status === 'valid' && tokenCheckMatches.message_content_intent === 'off';
  const tokenBlocked =
    DEFINITIVE_MISPASTE.includes(tokenClassification) ||
    tokenCheckMatches?.status === 'invalid' ||
    messageContentIntentOff;

  const openConnectDialog = (reconnect: boolean) => {
    if (interactionBlocked) return;
    capture(PostHogEventEnum.DiscordIntegrationConnectDialogOpened, { reconnect });
    setConnectDialogOpen(true);
  };

  const handleConnectDialogOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !mutation.isPending) {
      connectAttemptIdRef.current += 1;
      connectAttemptRef.current = false;
    }
    setConnectDialogOpen(nextOpen);
  };

  // Connect = authenticate by saving the token; like the OAuth sheets, connecting
  // also turns the bot on. Reuses persistParams so the start-poll/status UX fires.
  const handleConnect = async () => {
    const trimmed = trimmedToken;
    if (!trimmed || tokenBlocked || connecting || interactionBlocked || syncInFlightRef.current) {
      return;
    }
    // Cancel any pending debounced check so it doesn't fire a duplicate validate
    // POST alongside the inline check below.
    if (pendingCheckTimerRef.current) {
      clearTimeout(pendingCheckTimerRef.current);
      pendingCheckTimerRef.current = null;
    }
    connectAttemptRef.current = true;
    const attemptId = connectAttemptIdRef.current + 1;
    connectAttemptIdRef.current = attemptId;
    trackIntegrationConnectStarted(capture, 'discord');
    setConnecting(true);
    try {
      // Reuse a fresh paste-time verdict, otherwise check now. Only a definitive
      // Discord verdict blocks (a rejected token, or Message Content Intent off); 'unknown'
      // (or our backend erroring) fails open so connecting is never gated on a flaky round-trip.
      let verdict: DiscordTokenCheck | null = tokenCheck?.token === trimmed ? tokenCheck : null;
      if (!verdict) {
        try {
          verdict = { token: trimmed, ...(await validateTokenOnce(trimmed)) };
        } catch {
          verdict = null;
        }
      }
      if (
        connectAttemptIdRef.current !== attemptId ||
        trimmedTokenRef.current !== trimmed ||
        patchInFlightRef.current ||
        syncInFlightRef.current ||
        !statusBaselineReady
      ) {
        connectAttemptRef.current = false;
        return;
      }
      if (verdict?.status === 'invalid') {
        trackIntegrationConnectFailed(capture, 'discord', 'invalid_token_pre_save');
        connectAttemptRef.current = false;
        return; // the feedback line under the input explains; nothing was saved
      }
      if (verdict?.status === 'valid' && verdict.message_content_intent === 'off') {
        trackIntegrationConnectFailed(capture, 'discord', 'intent_off_pre_save');
        connectAttemptRef.current = false;
        return; // the gateway would refuse to start the bot; the warning offers a re-check
      }
      // token_saved marks a real save attempt, so emit it only past the pre-save
      // gate, right before the PATCH — not on the blocked path above.
      capture(PostHogEventEnum.DiscordIntegrationTokenSaved, { bot_active: true });
      persistParams({ discord_bot_token: trimmed, discord_bot_active: true }, 'starting');
    } finally {
      setConnecting(false);
    }
  };

  // Disconnect = remove the credential (de-authenticate); also turns the bot off.
  const handleDisconnect = () => {
    if (interactionBlocked || syncInFlightRef.current || patchInFlightRef.current) return;
    persistParams(
      { discord_bot_token: '', discord_bot_active: false },
      'stopping',
      undefined,
      disconnectMutation,
    );
  };

  // Map known Discord error codes to actionable guidance; unknown errors fall back to generic help plus the raw detail.
  const renderErrorGuidance = (error: string) => {
    // An error only shows once a token is saved, so deep-link straight to the user's
    // own application Bot page (Reset Token + intents live there) instead of the portal root.
    const devPortalHref = applicationId
      ? buildDiscordBotSettingsUrl(applicationId)
      : DISCORD_DEV_PORTAL_URL;
    if (error === DISCORD_ERROR_INVALID_TOKEN) {
      return (
        <Trans
          t={t}
          i18nKey="integration:discord.troubleshoot.invalid_token"
          components={[
            <strong key="label" />,
            <a
              key="link"
              href={devPortalHref}
              target="_blank"
              rel="noopener noreferrer"
              className={TROUBLESHOOT_LINK_CLASS}
            />,
            <a
              key="docs"
              href={DISCORD_DOCS_ANCHORS.invalidToken}
              target="_blank"
              rel="noopener noreferrer"
              className={TROUBLESHOOT_LINK_CLASS}
            />,
          ]}
        />
      );
    }
    if (error === DISCORD_ERROR_PRIVILEGED_INTENTS) {
      return (
        <Trans
          t={t}
          i18nKey="integration:discord.troubleshoot.privileged_intents"
          components={[
            <strong key="label" />,
            <a
              key="link"
              href={devPortalHref}
              target="_blank"
              rel="noopener noreferrer"
              className={TROUBLESHOOT_LINK_CLASS}
            />,
            <a
              key="docs"
              href={DISCORD_DOCS_ANCHORS.messageContentIntent}
              target="_blank"
              rel="noopener noreferrer"
              className={TROUBLESHOOT_LINK_CLASS}
            />,
          ]}
        />
      );
    }
    if (error === DISCORD_ERROR_DUPLICATE_APPLICATION) {
      return (
        <Trans
          t={t}
          i18nKey="integration:discord.troubleshoot.duplicate_application"
          components={[<strong key="label" />]}
        />
      );
    }
    return (
      <>
        <Trans
          t={t}
          i18nKey="integration:discord.troubleshoot.generic"
          components={[
            <strong key="label" />,
            <a
              key="guide"
              href={DISCORD_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={TROUBLESHOOT_LINK_CLASS}
            />,
            <a
              key="server"
              href={DISCORD_SERVER_INVITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={TROUBLESHOOT_LINK_CLASS}
            />,
          ]}
        />
        <span
          className="mt-2 block font-mono text-xs text-muted-foreground"
          data-testid="discord-advanced-error-message"
        >
          {error}
        </span>
      </>
    );
  };

  const feedbackLink = (href: string, key = 'link') => (
    <a
      key={key}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={TROUBLESHOOT_LINK_CLASS}
    />
  );

  // Paste-time feedback under the token input: specific wrong-paste guidance,
  // a live "checking" line, the resolved bot preview, or a Discord rejection.
  const renderTokenFeedback = () => {
    if (!connectDialogOpen || !trimmedToken) return null;
    if (DEFINITIVE_MISPASTE.includes(tokenClassification)) {
      // An App ID paste tells us the user's application, so link its Bot page directly.
      const botPageHref =
        tokenClassification === 'application_id'
          ? buildDiscordBotSettingsUrl(trimmedToken)
          : DISCORD_DEV_PORTAL_URL;
      return (
        <p className="text-sm text-destructive" data-testid="discord-advanced-token-feedback-error">
          <Trans
            t={t}
            i18nKey={`integration:discord.token_check.${tokenClassification}`}
            components={[feedbackLink(botPageHref)]}
          />
        </p>
      );
    }
    if (tokenCheckMatches?.status === 'invalid') {
      const derivedId = deriveDiscordApplicationId(trimmedToken);
      return (
        <p className="text-sm text-destructive" data-testid="discord-advanced-token-feedback-error">
          <Trans
            t={t}
            i18nKey="integration:discord.token_check.invalid"
            components={[
              feedbackLink(
                derivedId ? buildDiscordBotSettingsUrl(derivedId) : DISCORD_DEV_PORTAL_URL,
              ),
            ]}
          />
        </p>
      );
    }
    if (tokenCheckMatches?.status === 'valid') {
      const derivedId = deriveDiscordApplicationId(trimmedToken);
      const botPageHref = derivedId
        ? buildDiscordBotSettingsUrl(derivedId)
        : DISCORD_DEV_PORTAL_URL;
      return (
        <>
          <p
            className="flex items-center gap-1.5 text-sm text-success"
            data-testid="discord-advanced-token-feedback-valid"
          >
            <CheckCircle2 className="size-4 shrink-0" />
            {t('integration:discord.token_check.bot_found', {
              name: tokenCheckMatches.name,
              username: tokenCheckMatches.username,
            })}
          </p>
          {messageContentIntentOff && (
            <div
              className="mt-1 flex items-start gap-1.5 text-sm text-warning-foreground"
              data-testid="discord-advanced-token-feedback-intent-off"
            >
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              <div className="flex flex-col items-start gap-2">
                <span>
                  <Trans
                    t={t}
                    i18nKey="integration:discord.token_check.intent_off"
                    components={[
                      <strong key="label" className="font-semibold" />,
                      feedbackLink(botPageHref, 'settings'),
                      feedbackLink(DISCORD_DOCS_ANCHORS.messageContentIntent, 'docs'),
                    ]}
                  />
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={recheckToken}
                  loading={validateMutation.isPending}
                  data-testid="discord-advanced-token-recheck"
                >
                  <RefreshCw className="size-4" />
                  {t('integration:discord.token_check.recheck')}
                </Button>
              </div>
            </div>
          )}
        </>
      );
    }
    // 'unknown' = inconclusive (Discord unreachable / our backend erroring). Fail open: show a neutral line, never block connecting.
    if (tokenCheckMatches?.status === 'unknown') {
      return (
        <p
          className="flex items-center gap-1.5 text-sm text-muted-foreground"
          data-testid="discord-advanced-token-feedback-unknown"
        >
          <Info className="size-4 shrink-0" />
          {t('integration:discord.token_check.unknown')}
        </p>
      );
    }
    return (
      <p
        className="flex items-center gap-1.5 text-sm text-muted-foreground"
        data-testid="discord-advanced-token-feedback-checking"
      >
        <Spinner className="size-4 shrink-0" />
        {t('integration:discord.token_check.checking')}
      </p>
    );
  };
  const tokenFeedback = renderTokenFeedback();
  // Stable key per feedback kind so the slot cross-fades between states instead of hard-swapping.
  const feedbackKind = DEFINITIVE_MISPASTE.includes(tokenClassification)
    ? tokenClassification
    : messageContentIntentOff
      ? 'valid_intent_off'
      : (tokenCheckMatches?.status ?? 'checking');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className={cn('max-w-xl w-full h-full pb-0 space-y-0', contentClassName)}
        tabIndex={undefined}
        data-testid="discord-advanced-sheet"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          onOpenChange(false);
        }}
      >
        <SheetHeader className="px-0.5 items-center flex-row">
          <div className="flex items-center gap-3">
            <DiscordIcon className="size-8 shrink-0" />
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <SheetTitle data-testid="discord-advanced-sheet-title">
                  {t('integration:discord.title')}
                </SheetTitle>
                <IntegrationStatusBadge connected={isConnected} notRunning={botNotRunning} />
                {!botNotRunning && (
                  <IntegrationEnabledBadge
                    enabled={syncing ? enabledBeforeSyncRef.current : isEnabled}
                  />
                )}
              </div>
            </div>
          </div>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            aria-label={t('integrations:close')}
            data-testid="discord-advanced-close-button"
            className="shrink-0"
          >
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <div className="overflow-y-auto px-0.5 pb-6 space-y-6">
          <IntegrationIntroduction
            description={t('integration:discord.overview')}
            prerequisites={(
              t('integration:discord.prerequisites', { returnObjects: true }) as string[]
            ).map((_, index) => (
              <Trans
                key={index}
                t={t}
                i18nKey={`integration:discord.prerequisites.${index}`}
                components={[
                  <a
                    key="devportal"
                    href={DISCORD_DEV_PORTAL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={LINK_CLASS}
                  />,
                  <strong key="bold" className="font-semibold text-foreground" />,
                  <a
                    key="codegrant"
                    href={DISCORD_DOCS_ANCHORS.codeGrant}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={LINK_CLASS}
                  />,
                  <a
                    key="intent"
                    href={DISCORD_DOCS_ANCHORS.messageContentIntent}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={LINK_CLASS}
                  />,
                ]}
              />
            ))}
            docsHref="https://docs.quickchat.ai/channels/discord"
          />

          <IntegrationSection
            title={t('integrations:section.connect')}
            data-testid="discord-advanced-connect-enable-section"
          >
            {oneClickServers.length > 0 && (
              <Alert
                // Running both bots is a supported setup, so this only hardens into a warning
                // once the user's own bot is live and therefore actually being shadowed.
                variant={ownBotShadowed ? 'warning' : 'info'}
                data-testid="discord-advanced-oneclick-note"
                data-severity={ownBotShadowed ? 'warning' : 'info'}
              >
                {ownBotShadowed ? (
                  <AlertCircle className="size-4 !text-warning" />
                ) : (
                  <Info className="size-4 !text-info" />
                )}
                <AlertTitle className="mb-2">
                  {t('integration:discord.coexistence.title')}
                </AlertTitle>
                <AlertDescription>
                  <p className="m-0 text-muted-foreground">
                    <Trans
                      t={t}
                      i18nKey="integration:discord.coexistence.description"
                      values={{
                        servers: oneClickServers.map((guild) => guild.guild_name).join(', '),
                      }}
                      components={[
                        <a
                          key="docs"
                          href={DISCORD_DOCS_ANCHORS.coexistence}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={TROUBLESHOOT_LINK_CLASS}
                        />,
                      ]}
                    />
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="mt-4 w-full"
                    onClick={onBack}
                    data-testid="discord-advanced-back-to-oneclick"
                  >
                    <ArrowLeft className="size-4" />
                    {t('integration:discord.coexistence.back_link')}
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {/* The enable toggle that used to carry this spinner now lives on the one-click sheet. */}
            {syncing && (
              <Alert variant="info" data-testid="discord-advanced-sync-alert">
                <Spinner
                  className="size-4 shrink-0 text-info"
                  data-testid="discord-advanced-sync-loader"
                />
                <AlertDescription>{t('integration:discord.sync_in_progress')}</AlertDescription>
              </Alert>
            )}

            {(syncUnconfirmed || serverSyncPending) && (
              <Alert variant="info" data-testid="discord-advanced-sync-pending-alert">
                <Info className="size-4 !text-info" />
                <AlertDescription>{t('integration:discord.sync_timeout')}</AlertDescription>
              </Alert>
            )}

            {params?.discord_bot_errors && !syncing && !syncUnconfirmed && !serverSyncPending && (
              <Alert variant="destructive" data-testid="discord-advanced-error-alert">
                <AlertCircle className="size-4 !text-destructive" />
                <AlertTitle>{t('integration:discord.errors.heading')}</AlertTitle>
                <AlertDescription>
                  {renderErrorGuidance(params.discord_bot_errors)}
                </AlertDescription>
              </Alert>
            )}

            {isConnected ? (
              <IntegrationConnectionCard
                data-testid="discord-advanced-connection-card"
                details={[
                  {
                    label: t('integration:discord.bot_name_label'),
                    value: botInfoPending ? (
                      <Spinner
                        className="size-3.5 text-foreground-subtle"
                        data-testid="discord-advanced-bot-name-loader"
                      />
                    ) : (
                      (botInfo?.name ?? '')
                    ),
                    'data-testid': 'discord-advanced-bot-name',
                  },
                  {
                    label: t('integration:discord.bot_username_label'),
                    value: botInfoPending ? (
                      <Spinner
                        className="size-3.5 text-foreground-subtle"
                        data-testid="discord-advanced-bot-username-loader"
                      />
                    ) : botInfo?.username ? (
                      `@${botInfo.username}`
                    ) : (
                      ''
                    ),
                    'data-testid': 'discord-advanced-bot-username',
                  },
                  {
                    label: t('integration:discord.application_id_label'),
                    value: applicationId ?? '',
                    'data-testid': 'discord-advanced-application-id',
                  },
                ]}
                actions={
                  <>
                    {applicationId && !botNotRunning && (
                      <Button asChild variant="secondary" size="sm" className="flex-1">
                        <a
                          href={buildDiscordInviteUrl(applicationId)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() =>
                            capture(PostHogEventEnum.DiscordIntegrationInviteLinkClicked)
                          }
                          data-testid="discord-advanced-invite-link"
                        >
                          <Plus className="size-4" />
                          {t('integration:discord.add_to_server')}
                        </a>
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="flex-1"
                      onClick={() => openConnectDialog(true)}
                      disabled={interactionBlocked}
                      data-testid="discord-advanced-reconnect-button"
                    >
                      <RefreshCw className="size-4" />
                      {t('integrations:reconnect')}
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      className="flex-1"
                      onClick={handleDisconnect}
                      disabled={interactionBlocked}
                      data-testid="discord-advanced-disconnect-button"
                    >
                      <Unplug className="size-4" />
                      {t('integrations:disconnect')}
                    </Button>
                  </>
                }
              >
                {/* A saved token is what renders this card, so only the health line can say
                    whether the bot actually works. */}
                <DiscordBotHealthLine
                  status={botInfo}
                  loading={botInfoPending}
                  error={botInfoError}
                />
              </IntegrationConnectionCard>
            ) : (
              <Button
                type="button"
                variant="dark"
                size="sm"
                className="w-full"
                onClick={() => openConnectDialog(false)}
                disabled={interactionBlocked}
                data-testid="discord-advanced-connect-button"
              >
                <Plug className="size-4" />
                {t('integrations:connect', { name: 'Discord' })}
              </Button>
            )}
          </IntegrationSection>

          <DiscordAdvancedAutomatedMessages
            open={open}
            enabled={isConnected}
            blocked={interactionBlocked}
            hideEmptyState={botNotRunning}
          />
        </div>

        <IntegrationConnectDialog
          open={connectDialogOpen}
          onOpenChange={handleConnectDialogOpenChange}
          title={t('integrations:connect', { name: 'Discord' })}
          description={
            <Trans
              t={t}
              i18nKey="integration:discord.connect_description"
              components={[
                <a
                  key="portal"
                  href={DISCORD_DEV_PORTAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={LINK_CLASS}
                />,
              ]}
            />
          }
          submitLabel={t('integrations:connect', { name: 'Discord' })}
          submitIcon={<Plug className="size-4" />}
          onSubmit={handleConnect}
          submitDisabled={!trimmedToken || tokenBlocked || connecting || interactionBlocked}
          isSubmitting={connecting || mutation.isPending}
          data-testid="discord-advanced-connect-dialog"
        >
          <div className="rounded-lg bg-muted p-4" data-testid="discord-advanced-token-guide">
            <div className="space-y-3">
              <p className="m-0 text-sm font-medium text-foreground">
                {t('integration:discord.token_guide.title')}
              </p>
              <ol className="m-0 list-decimal space-y-1 pl-5 text-sm text-muted-foreground marker:text-foreground-subtle">
                {(
                  t('integration:discord.token_guide.steps', { returnObjects: true }) as string[]
                ).map((_, index) => (
                  <li key={index}>
                    <Trans
                      t={t}
                      i18nKey={`integration:discord.token_guide.steps.${index}`}
                      components={[
                        <a
                          key="portal"
                          href={DISCORD_DEV_PORTAL_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={LINK_CLASS}
                        />,
                        <strong key="bold" className="font-semibold text-foreground" />,
                      ]}
                    />
                  </li>
                ))}
              </ol>
            </div>
            <div className="mt-4 space-y-2">
              <div
                className="flex flex-wrap items-center gap-x-2 gap-y-1.5"
                aria-hidden="true"
                data-testid="discord-advanced-token-example"
              >
                <span className="text-xs text-foreground-subtle">
                  {t('integration:discord.token_guide.example')}
                </span>
                <code className="rounded-md border border-border bg-card px-2 py-1 font-mono text-xs text-muted-foreground">
                  <span>MjMx…NjE</span>
                  <span className="font-semibold text-foreground">.</span>
                  <span>GaBcDe</span>
                  <span className="font-semibold text-foreground">.</span>
                  <span>f9Kv…zXy9</span>
                </code>
              </div>
              <p className="m-0 text-xs text-foreground-subtle">
                {t('integration:discord.token_guide.format_hint')}
              </p>
            </div>
          </div>

          <Controller
            control={form.control}
            name="discord_bot_token"
            render={({ field }) => (
              <div className="space-y-1.5">
                <Label htmlFor="discord-advanced-bot-token-input">
                  {t('integration:discord.bot_token.label')}
                </Label>
                <Input
                  id="discord-advanced-bot-token-input"
                  autoComplete="off"
                  data-1p-ignore
                  data-lpignore="true"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  placeholder={t('integration:discord.bot_token.placeholder')}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  data-testid="discord-advanced-bot-token-input"
                  className="w-full"
                />
                <div
                  className={cn(!messageContentIntentOff && 'relative min-h-10')}
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <AnimatePresence initial={false}>
                    {tokenFeedback && (
                      <motion.div
                        key={feedbackKind}
                        className={cn(
                          messageContentIntentOff ? 'relative' : 'absolute inset-x-0 top-0',
                        )}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15, ease: 'easeOut' }}
                      >
                        {tokenFeedback}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}
          />
        </IntegrationConnectDialog>
      </SheetContent>
    </Sheet>
  );
};
